import { useState, useEffect } from 'react';
import { Sidebar } from '../../components/feature/Sidebar';
import { TopBar } from '../../components/feature/TopBar';
import { Button } from '../../components/atoms/Button';

interface DeviceData {
  id: string;
  name: string;
  type: 'plant' | 'inverter';
  status: 'online' | 'offline' | 'warning' | 'error';
  power: number;
  temperature?: number;
  soc?: number;
  voltage?: number;
  current?: number;
  plantId?: string;
  model?: string;
  lastUpdate: string;
}

const DevicesPage = () => {
  const [devices, setDevices] = useState<DeviceData[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [selectedPlant, setSelectedPlant] = useState<string | null>(null);
  const [plantQuery, setPlantQuery] = useState('');
  const [inverterQuery, setInverterQuery] = useState('');
  const [plantStatus, setPlantStatus] = useState<'all' | 'online' | 'warning' | 'error' | 'offline'>('all');
  const [inverterStatus, setInverterStatus] = useState<'all' | 'online' | 'warning' | 'error' | 'offline'>('all');
  const [plantSort, setPlantSort] = useState<'name' | 'power'>('name');
  const [inverterSort, setInverterSort] = useState<'name' | 'power'>('power');
  const [plantVisible, setPlantVisible] = useState(20);
  const [inverterVisible, setInverterVisible] = useState(20);

  useEffect(() => {
    const mockDevices: DeviceData[] = [
      // Planta Norte
      {
        id: 'pl_001',
        name: 'Planta Solar Norte',
        type: 'plant',
        status: 'online',
        power: 12500,
        lastUpdate: new Date().toLocaleTimeString()
      },
      {
        id: 'inv_001',
        name: 'Fronius Symo 10.0-3-M',
        type: 'inverter',
        status: 'online',
        power: 8500,
        temperature: 45,
        voltage: 400,
        current: 21.2,
        plantId: 'pl_001',
        model: 'Symo 10.0-3-M',
        lastUpdate: new Date().toLocaleTimeString()
      },
      {
        id: 'inv_002',
        name: 'Fronius Primo 5.0-1',
        type: 'inverter',
        status: 'online',
        power: 4000,
        temperature: 42,
        voltage: 230,
        current: 17.4,
        plantId: 'pl_001',
        model: 'Primo 5.0-1',
        lastUpdate: new Date().toLocaleTimeString()
      },

      // Planta Sur
      {
        id: 'pl_002',
        name: 'Planta Solar Sur',
        type: 'plant',
        status: 'warning',
        power: 8300,
        lastUpdate: new Date().toLocaleTimeString()
      },
      {
        id: 'inv_003',
        name: 'Fronius Symo 8.2-3-M',
        type: 'inverter',
        status: 'online',
        power: 6100,
        temperature: 43,
        voltage: 380,
        current: 16.0,
        plantId: 'pl_002',
        model: 'Symo 8.2-3-M',
        lastUpdate: new Date().toLocaleTimeString()
      },
      {
        id: 'inv_004',
        name: 'Fronius Galvo 3.0-1',
        type: 'inverter',
        status: 'warning',
        power: 2200,
        temperature: 55,
        voltage: 225,
        current: 9.8,
        plantId: 'pl_002',
        model: 'Galvo 3.0-1',
        lastUpdate: new Date().toLocaleTimeString()
      },

      // Planta Este
      {
        id: 'pl_003',
        name: 'Planta Solar Este',
        type: 'plant',
        status: 'online',
        power: 15000,
        lastUpdate: new Date().toLocaleTimeString()
      },
      {
        id: 'inv_005',
        name: 'Fronius Eco 27.0-3-S',
        type: 'inverter',
        status: 'online',
        power: 15000,
        temperature: 48,
        voltage: 410,
        current: 36.5,
        plantId: 'pl_003',
        model: 'Eco 27.0-3-S',
        lastUpdate: new Date().toLocaleTimeString()
      }
    ];

    setDevices(mockDevices);
    const firstPlant = mockDevices.find(d => d.type === 'plant');
    setSelectedPlant(firstPlant ? firstPlant.id : null);
    setSelectedDevice(firstPlant ? firstPlant.id : null);
  }, []);

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'plant': return 'ri-sun-line';
      case 'inverter': return 'ri-flashlight-line';
      default: return 'ri-device-line';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-500 bg-green-100';
      case 'warning': return 'text-yellow-500 bg-yellow-100';
      case 'error': return 'text-red-500 bg-red-100';
      case 'offline': return 'text-gray-500 bg-gray-100';
      default: return 'text-gray-500 bg-gray-100';
    }
  };



  const selectedDeviceData = devices.find(d => d.id === selectedDevice);
  const allPlants = devices.filter(d => d.type === 'plant');
  const plantsFiltered = allPlants
    .filter(p => plantStatus === 'all' || p.status === plantStatus)
    .filter(p => p.name.toLowerCase().includes(plantQuery.toLowerCase()));
  const plantsSorted = [...plantsFiltered].sort((a, b) => plantSort === 'name' ? a.name.localeCompare(b.name) : b.power - a.power);
  const visiblePlants = plantsSorted.slice(0, plantVisible);

  const allInverters = devices.filter(d => d.type === 'inverter' && (!selectedPlant || d.plantId === selectedPlant));
  const invertersFiltered = allInverters
    .filter(i => inverterStatus === 'all' || i.status === inverterStatus)
    .filter(i => i.name.toLowerCase().includes(inverterQuery.toLowerCase()) || (i.model ? i.model.toLowerCase().includes(inverterQuery.toLowerCase()) : false));
  const invertersSorted = [...invertersFiltered].sort((a, b) => inverterSort === 'name' ? a.name.localeCompare(b.name) : b.power - a.power);
  const visibleInverters = invertersSorted.slice(0, inverterVisible);

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 ml-[var(--sidebar-width)]">
        <TopBar title="Plantas e Inversores" />
        <div className="p-6 space-y-6">
          {/* Hero Section */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-900 to-indigo-900 shadow-xl p-8">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center mix-blend-overlay"></div>
            </div>
            <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Plantas e Inversores</h1>
                <p className="text-blue-100 max-w-xl">
                  Gestión centralizada de todos tus activos de generación solar.
                </p>
              </div>
              <div className="flex items-center space-x-2 text-sm text-blue-100 bg-white/10 backdrop-blur-md px-4 py-2 rounded-xl border border-white/20">
                <i className="ri-refresh-line animate-spin-slow"></i>
                <span>Actualizado: {new Date().toLocaleTimeString()}</span>
              </div>
            </div>
          </div>

          {/* Status Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {['online', 'warning', 'error', 'offline'].map(status => {
              const count = devices.filter(d => d.status === status).length;
              // colors variable removed as it was unused
              const textColors = {
                online: 'text-green-600',
                warning: 'text-yellow-600',
                error: 'text-red-600',
                offline: 'text-gray-600'
              } as const;
              const bgColors = {
                online: 'bg-green-50',
                warning: 'bg-yellow-50',
                error: 'bg-red-50',
                offline: 'bg-gray-50'
              } as const;
              const labels = { online: 'En Línea', warning: 'Advertencia', error: 'Error', offline: 'Desconectado' } as const;
              const icons = { online: 'ri-checkbox-circle-line', warning: 'ri-alert-line', error: 'ri-error-warning-line', offline: 'ri-wifi-off-line' } as const;

              return (
                <div key={status} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-all group">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">{labels[status as keyof typeof labels]}</p>
                      <p className="text-3xl font-bold text-gray-900">{count}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${bgColors[status as keyof typeof bgColors]} group-hover:scale-110 transition-transform`}>
                      <i className={`${icons[status as keyof typeof icons]} text-2xl ${textColors[status as keyof typeof textColors]}`}></i>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Plants List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-[calc(100vh-340px)] flex flex-col">
                <div className="p-5 border-b border-gray-100 bg-gray-50/50 space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <i className="ri-sun-line text-orange-500"></i>
                      Plantas
                    </h2>
                    <span className="text-xs font-medium bg-gray-200 text-gray-600 px-2 py-1 rounded-full">{visiblePlants.length}</span>
                  </div>
                  <div className="space-y-3">
                    <div className="relative">
                      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      <input
                        value={plantQuery}
                        onChange={e => setPlantQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder="Buscar planta..."
                      />
                    </div>
                    <div className="flex space-x-2">
                      <select value={plantStatus} onChange={e => setPlantStatus(e.target.value as any)} className="flex-1 bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer">
                        <option value="all">Estado: Todos</option>
                        <option value="online">En línea</option>
                        <option value="warning">Advertencia</option>
                        <option value="error">Error</option>
                        <option value="offline">Desconectado</option>
                      </select>
                      <select value={plantSort} onChange={e => setPlantSort(e.target.value as any)} className="flex-1 bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer">
                        <option value="name">Orden: Nombre</option>
                        <option value="power">Orden: Potencia</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {visiblePlants.map(plant => {
                    const invCount = devices.filter(d => d.type === 'inverter' && d.plantId === plant.id).length;
                    const isActive = selectedPlant === plant.id;
                    return (
                      <div
                        key={plant.id}
                        className={`p-4 rounded-xl cursor-pointer transition-all border ${isActive
                          ? 'bg-blue-50 border-blue-200 shadow-sm'
                          : 'bg-white border-transparent hover:bg-gray-50 hover:border-gray-200'
                          }`}
                        onClick={() => { setSelectedPlant(plant.id); setSelectedDevice(plant.id); }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isActive ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                              <i className={`${getDeviceIcon('plant')} text-xl`}></i>
                            </div>
                            <div>
                              <h3 className={`font-bold ${isActive ? 'text-blue-900' : 'text-gray-900'}`}>{plant.name}</h3>
                              <p className={`text-xs ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>{invCount} inversores</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-1 ${getStatusColor(plant.status)}`}>
                              {plant.status === 'online' && 'En Línea'}
                              {plant.status === 'warning' && 'Advertencia'}
                              {plant.status === 'error' && 'Error'}
                              {plant.status === 'offline' && 'Offline'}
                            </div>
                            <p className="text-sm font-bold text-gray-900">{(plant.power / 1000).toFixed(1)} kW</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {plantsSorted.length > plantVisible && (
                    <button className="w-full py-3 text-sm text-blue-600 font-medium hover:bg-blue-50 rounded-xl transition-colors" onClick={() => setPlantVisible(v => v + 20)}>
                      Cargar más plantas
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Inverters List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-[calc(100vh-340px)] flex flex-col">
                <div className="p-5 border-b border-gray-100 bg-gray-50/50 space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <i className="ri-flashlight-line text-purple-500"></i>
                      Inversores
                    </h2>
                    <span className="text-xs font-medium bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                      {selectedPlant ? visibleInverters.length : 0}
                    </span>
                  </div>

                  {selectedPlant && (
                    <div className="space-y-3">
                      <div className="relative">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                        <input
                          value={inverterQuery}
                          onChange={e => setInverterQuery(e.target.value)}
                          className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                          placeholder="Buscar inversor..."
                        />
                      </div>
                      <div className="flex space-x-2">
                        <select value={inverterStatus} onChange={e => setInverterStatus(e.target.value as any)} className="flex-1 bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 cursor-pointer">
                          <option value="all">Estado: Todos</option>
                          <option value="online">En línea</option>
                          <option value="warning">Advertencia</option>
                          <option value="error">Error</option>
                          <option value="offline">Desconectado</option>
                        </select>
                        <select value={inverterSort} onChange={e => setInverterSort(e.target.value as any)} className="flex-1 bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 cursor-pointer">
                          <option value="power">Orden: Potencia</option>
                          <option value="name">Orden: Nombre</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto p-3">
                  {!selectedPlant ? (
                    <div className="h-full flex flex-col items-center justify-center text-center p-6 text-gray-400">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <i className="ri-arrow-left-line text-2xl"></i>
                      </div>
                      <p className="font-medium text-gray-600">Selecciona una planta</p>
                      <p className="text-sm mt-1">Elige una planta de la lista para ver sus inversores asociados.</p>
                    </div>
                  ) : visibleInverters.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center p-6 text-gray-400">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <i className="ri-filter-off-line text-2xl"></i>
                      </div>
                      <p className="font-medium text-gray-600">No se encontraron inversores</p>
                      <p className="text-sm mt-1">Intenta cambiar los filtros de búsqueda.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {visibleInverters.map(inv => {
                        const isActive = selectedDevice === inv.id;
                        return (
                          <div
                            key={inv.id}
                            className={`p-4 rounded-xl cursor-pointer transition-all border ${isActive
                              ? 'bg-purple-50 border-purple-200 shadow-sm'
                              : 'bg-white border-gray-100 hover:bg-gray-50 hover:border-gray-200'
                              }`}
                            onClick={() => setSelectedDevice(inv.id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isActive ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                                  <i className={`${getDeviceIcon('inverter')} text-xl`}></i>
                                </div>
                                <div>
                                  <h3 className={`font-bold text-sm ${isActive ? 'text-purple-900' : 'text-gray-900'}`}>{inv.name}</h3>
                                  <p className={`text-xs ${isActive ? 'text-purple-600' : 'text-gray-500'}`}>{inv.model}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-1 ${getStatusColor(inv.status)}`}>
                                  {inv.status === 'online' && 'En Línea'}
                                  {inv.status === 'warning' && 'Advertencia'}
                                  {inv.status === 'error' && 'Error'}
                                  {inv.status === 'offline' && 'Offline'}
                                </div>
                                <p className="text-sm font-bold text-gray-900">{(inv.power / 1000).toFixed(1)} kW</p>
                              </div>
                            </div>
                            <div className="mt-3 grid grid-cols-3 gap-2 text-xs border-t border-gray-100/50 pt-2">
                              <div className="flex flex-col">
                                <span className="text-gray-400">Voltaje</span>
                                <span className="font-medium text-gray-700">{inv.voltage?.toFixed(0)}V</span>
                              </div>
                              <div className="flex flex-col border-l border-gray-100 pl-2">
                                <span className="text-gray-400">Corriente</span>
                                <span className="font-medium text-gray-700">{inv.current?.toFixed(1)}A</span>
                              </div>
                              <div className="flex flex-col border-l border-gray-100 pl-2">
                                <span className="text-gray-400">Temp</span>
                                <span className="font-medium text-gray-700">{inv.temperature?.toFixed(0)}°C</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {invertersSorted.length > inverterVisible && (
                        <button className="w-full py-3 text-sm text-purple-600 font-medium hover:bg-purple-50 rounded-xl transition-colors" onClick={() => setInverterVisible(v => v + 20)}>
                          Cargar más inversores
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Details Panel */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-[calc(100vh-340px)] flex flex-col">
                <div className="p-5 border-b border-gray-100 bg-gray-50/50">
                  <h2 className="text-lg font-bold text-gray-900">Detalles del Dispositivo</h2>
                  <div className="text-xs font-medium text-gray-500 mt-1 flex items-center gap-1">
                    {selectedDevice && devices.find(d => d.id === selectedDevice)?.type === 'inverter' ? (
                      <>
                        <i className="ri-sun-line"></i>
                        <span>{devices.find(d => d.id === selectedPlant)?.name || ''}</span>
                        <i className="ri-arrow-right-s-line"></i>
                        <i className="ri-flashlight-line"></i>
                        <span>{devices.find(d => d.id === selectedDevice)?.name || ''}</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-sun-line"></i>
                        <span>{devices.find(d => d.id === selectedPlant)?.name || 'Selecciona una planta'}</span>
                      </>
                    )}
                  </div>
                </div>

                {selectedDeviceData ? (
                  <div className="flex-1 overflow-y-auto p-6">
                    <div className="flex flex-col items-center text-center mb-8">
                      <div className={`w-24 h-24 rounded-2xl flex items-center justify-center mb-4 shadow-lg ${selectedDeviceData.type === 'plant'
                        ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white'
                        : 'bg-gradient-to-br from-purple-500 to-pink-600 text-white'
                        }`}>
                        <i className={`${getDeviceIcon(selectedDeviceData.type)} text-5xl`}></i>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{selectedDeviceData.name}</h3>
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getStatusColor(selectedDeviceData.status)}`}>
                        {selectedDeviceData.status === 'online' && 'En Línea'}
                        {selectedDeviceData.status === 'warning' && 'Advertencia'}
                        {selectedDeviceData.status === 'error' && 'Error'}
                        {selectedDeviceData.status === 'offline' && 'Desconectado'}
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Métricas Principales</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                            <p className="text-xs text-gray-500 mb-1">Potencia Actual</p>
                            <p className="text-lg font-bold text-gray-900">{(selectedDeviceData.power / 1000).toFixed(2)} <span className="text-xs font-medium text-gray-500">kW</span></p>
                          </div>
                          {selectedDeviceData.type === 'inverter' && selectedDeviceData.temperature && (
                            <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                              <p className="text-xs text-gray-500 mb-1">Temperatura</p>
                              <p className="text-lg font-bold text-gray-900">{selectedDeviceData.temperature.toFixed(1)} <span className="text-xs font-medium text-gray-500">°C</span></p>
                            </div>
                          )}
                          {selectedDeviceData.type === 'plant' && (
                            <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                              <p className="text-xs text-gray-500 mb-1">Inversores</p>
                              <p className="text-lg font-bold text-gray-900">{devices.filter(d => d.type === 'inverter' && d.plantId === selectedDeviceData.id).length}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-3">
                        <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Información Técnica</h4>

                        {selectedDeviceData.type === 'plant' && (
                          <div className="flex justify-between items-center py-3 border-b border-gray-100">
                            <span className="text-sm text-gray-600">Potencia Total Inversores</span>
                            <span className="text-sm font-bold text-gray-900">
                              {(devices.filter(d => d.type === 'inverter' && d.plantId === selectedDeviceData.id).reduce((sum, d) => sum + d.power, 0) / 1000).toFixed(2)} kW
                            </span>
                          </div>
                        )}

                        {selectedDeviceData.type === 'inverter' && (
                          <>
                            {selectedDeviceData.voltage && (
                              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                                <span className="text-sm text-gray-600">Voltaje AC</span>
                                <span className="text-sm font-bold text-gray-900">{selectedDeviceData.voltage.toFixed(1)} V</span>
                              </div>
                            )}
                            {selectedDeviceData.current && (
                              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                                <span className="text-sm text-gray-600">Corriente AC</span>
                                <span className="text-sm font-bold text-gray-900">{selectedDeviceData.current.toFixed(1)} A</span>
                              </div>
                            )}
                            {selectedDeviceData.soc && (
                              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                                <span className="text-sm text-gray-600">Estado de Carga</span>
                                <span className="text-sm font-bold text-gray-900">{selectedDeviceData.soc.toFixed(0)}%</span>
                              </div>
                            )}
                          </>
                        )}

                        <div className="flex justify-between items-center py-3 border-b border-gray-100">
                          <span className="text-sm text-gray-600">Última Actualización</span>
                          <span className="text-sm font-medium text-gray-900">{selectedDeviceData.lastUpdate}</span>
                        </div>
                      </div>

                      <div className="pt-4 space-y-3">
                        <Button variant="primary" size="lg" className="w-full justify-center shadow-lg shadow-blue-200">
                          <i className="ri-line-chart-line mr-2"></i>
                          Ver Histórico Detallado
                        </Button>
                        <Button variant="secondary" size="lg" className="w-full justify-center bg-white border-gray-200 hover:bg-gray-50">
                          <i className="ri-settings-3-line mr-2"></i>
                          Configuración
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center p-8 text-gray-400">
                    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                      <i className="ri-cursor-line text-3xl text-gray-300"></i>
                    </div>
                    <p className="font-medium text-gray-600">Selecciona un dispositivo</p>
                    <p className="text-sm mt-1 max-w-[200px]">Haz clic en una planta o inversor para ver sus detalles completos.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevicesPage;